/**
 * Блок «Кредиторы» (ТЗ п. 5.2.5, правила R-04, R-05).
 *
 * Итог рассчитывается по таблице и не редактируется вручную: это то самое
 * место, где расхождение между текстом заявления и списком кредиторов
 * блокирует генерацию (FR-27).
 *
 * Кредитор подбирается из локального справочника по первым символам (FR-12),
 * а новую организацию можно завести по ИНН через DaData (FR-13) — при этом
 * она сохраняется в локальный справочник и дальше живёт независимо
 * от внешнего сервиса.
 *
 * Кредитор, набранный вручную, попадает в справочник сам — при формировании
 * документов (packages/domain/src/rules/creditor-dictionary.ts).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { computeTotals, newRowId, type Creditor, type ObligatoryPayment } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api } from '../../../api.js';
import { DataGrid, type ColumnDef } from '../../../components/DataGrid.js';
import { kopecksToText, textToKopecks } from '../../../components/fields/index.js';
import type { BlockProps } from './types.js';
import { noteDictionaryFailure } from '../../../storage/dictionary-health.js';

interface DictionaryEntry {
  id: string;
  name: string;
  inn?: string;
  address?: string;
}

/** Ячейка с подбором кредитора из справочника (FR-12). */
function CreditorNameCell({
  row,
  onChange,
  onKeyDown,
  readOnly,
  entries,
}: {
  row: Creditor;
  onChange: (row: Creditor) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  readOnly: boolean;
  entries: DictionaryEntry[];
}) {
  const [open, setOpen] = useState(false);
  const matches = useMemo(() => {
    const query = (row.name ?? '').trim().toLowerCase();
    if (query.length < 2) return [];
    return entries.filter((e) => e.name.toLowerCase().includes(query) || (e.inn ?? '').startsWith(query)).slice(0, 8);
  }, [row.name, entries]);

  return (
    <div className="cell-with-suggestions">
      <input
        value={row.name ?? ''}
        readOnly={readOnly}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          // ИНН выбранной из справочника записи к исправленному наименованию
          // уже не относится; набранный вручную — остаётся
          onChange({ ...row, name: e.target.value, dictionaryId: null, inn: row.dictionaryId ? '' : row.inn });
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && matches.length > 0 && (
        <ul className="suggestions">
          {matches.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  // Вместе с наименованием подставляется адрес (FR-12)
                  onChange({ ...row, name: entry.name, dictionaryId: entry.id, inn: entry.inn ?? '', address: entry.address ?? row.address });
                  setOpen(false);
                }}
              >
                {entry.name}
                {entry.inn && <span className="hint"> ИНН {entry.inn}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CreditorsBlock({ profile, update, readOnly, errorFor }: BlockProps) {
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [bases, setBases] = useState<string[]>([]);
  const [documents, setDocuments] = useState<string[]>([]);
  const [importInn, setImportInn] = useState('');
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    void Promise.all([
      api.dictionary('creditors', { limit: 500 }).then((r) => setEntries(r.entries as DictionaryEntry[])),
      api.dictionary('obligation-types').then((r) => setTypes(r.entries.map((e) => e.name))),
      api.dictionary('obligation-bases').then((r) => setBases(r.entries.map((e) => e.name))),
      api.dictionary('confirming-documents').then((r) => setDocuments(r.entries.map((e) => e.name))),
    ]).catch(noteDictionaryFailure);
  }, []);

  const totals = computeTotals(profile);

  /** Ввоз организации по ИНН в локальный справочник (FR-13). */
  const importByInn = async () => {
    setImportMessage(null);
    try {
      const result = await api.dadata.importParty(importInn.replace(/\D/g, ''));
      const entry = result.entry as DictionaryEntry;
      setEntries((current) => [entry, ...current.filter((e) => e.id !== entry.id)]);
      setImportMessage(
        [
          result.created ? `Добавлено в справочник: ${entry.name}` : `Уже есть в справочнике: ${entry.name}`,
          result.warning,
        ]
          .filter(Boolean)
          .join('. '),
      );
      setImportInn('');
    } catch (error) {
      setImportMessage((error as Error).message);
    }
  };

  /*
    Ширины столбцов в сумме дают 100 %.

    Раньше выходило 120 %: значения ставились на глаз, каждое по отдельности
    выглядело разумным. Браузер такую таблицу ужимает пропорционально, и длинные
    названия столбцов рвались посреди слова — «Задолженнос / ть, ₽».

    Двум столбцам с флажками названия сокращены: столбец шириной в сам флажок
    не вместит «Просрочка более трёх месяцев» никак. Полное пояснение
    у них в подсказке.

    Наименование кредитора и место нахождения ПЕРЕНОСЯТ текст по строкам.
    Это самые длинные значения в таблице — «ООО Профессиональная коллекторская
    организация…» и почтовый адрес с индексом, корпусом и офисом, — и сверять
    их приходится с договором глазами. Расширять под них таблицу бесполезно:
    граф десять, и любое наименование окажется длиннее.

    Остальным графам хватает одной строки: суммы и реквизиты короткие,
    а содержание, основание и вид документа выбираются из списка.
  */
  const columns: Array<ColumnDef<Creditor>> = [
    {
      key: 'obligationType',
      title: 'Содержание обязательства',
      hint: 'Выберите из списка или впишите своё: перечень не закрыт',
      width: 10,
      type: 'combo',
      options: types,
      get: (row) => row.obligationType,
      set: (row, value) => ({ ...row, obligationType: value }),
    },
    {
      key: 'name',
      title: 'Кредитор',
      width: 16,
      type: 'multiline',
      get: (row) => row.name,
      set: (row, value) => ({ ...row, name: value }),
      render: (props) => <CreditorNameCell {...props} entries={entries} />,
    },
    {
      // В документы ИНН не печатается — форма его не предусматривает.
      // Он нужен справочнику: по нему кредитор узнаётся в следующих делах,
      // даже если наименование набрано иначе
      key: 'inn',
      title: 'ИНН',
      hint: 'ИНН организации-кредитора. В документы не выводится, сохраняется в справочник кредиторов',
      width: 8,
      get: (row) => row.inn ?? '',
      set: (row, value) => ({ ...row, inn: String(value).replace(/\D/g, '').slice(0, 12) }),
    },
    {
      key: 'address',
      title: 'Место нахождения',
      width: 16,
      type: 'multiline',
      get: (row) => row.address,
      set: (row, value) => ({ ...row, address: value }),
    },
    {
      key: 'basis',
      title: 'Основание',
      hint: 'Выберите из списка или впишите своё: перечень не закрыт',
      width: 9,
      type: 'combo',
      options: bases,
      get: (row) => row.basis,
      set: (row, value) => ({ ...row, basis: value }),
    },
    {
      key: 'basisDetails',
      title: 'Реквизиты основания',
      width: 7,
      get: (row) => row.basisDetails,
      set: (row, value) => ({ ...row, basisDetails: value }),
    },
    {
      // Графа формы «в том числе задолженность». Именно она идёт в текст
      // заявления и в общую сумму (решение по вопросу 20)
      key: 'amountDebt',
      title: 'Задолженность, ₽',
      width: 9,
      type: 'money',
      align: 'right',
      get: (row) => kopecksToText(row.amountDebt),
      set: (row, value) => ({ ...row, amountDebt: textToKopecks(value) }),
    },
    {
      // Графа формы «всего» — сумма при возникновении обязательства.
      // Заполняется, только если известна
      key: 'amountTotal',
      title: 'Всего при возникновении, ₽',
      width: 9,
      type: 'money',
      align: 'right',
      get: (row) => kopecksToText(row.amountTotal ?? 0),
      set: (row, value) => ({ ...row, amountTotal: textToKopecks(value) || undefined }),
    },
    {
      key: 'confirmingDocument',
      title: 'Подтверждающий документ',
      hint: 'Выберите из списка или впишите своё: перечень не закрыт',
      width: 9,
      type: 'combo',
      options: documents,
      get: (row) => row.confirmingDocument,
      set: (row, value) => ({ ...row, confirmingDocument: value }),
    },
    {
      key: 'overdueOverThreeMonths',
      title: '> 3 мес.',
      hint: 'Просрочка исполнения более трёх месяцев (признак банкротства, ст. 213.3 Закона о банкротстве)',
      width: 4,
      type: 'checkbox',
      align: 'center',
      get: (row) => row.overdueOverThreeMonths,
      set: (row, value) => ({ ...row, overdueOverThreeMonths: Boolean(value) }),
    },
    {
      key: 'isBusiness',
      title: 'ИП',
      hint: 'Обязательство возникло из предпринимательской деятельности',
      width: 3,
      type: 'checkbox',
      align: 'center',
      get: (row) => row.isBusiness,
      set: (row, value) => ({ ...row, isBusiness: Boolean(value) }),
    },
  ];

  // Ошибки строк из панели готовности: путь вида creditors[0].name
  const rowErrors: Record<number, string[]> = {};
  profile.creditors.forEach((_, index) => {
    const keys = columns.map((c) => c.key).filter((key) => errorFor(`creditors[${index}].${key}`));
    if (keys.length) rowErrors[index] = keys;
  });

  return (
    <section className="block">
      <h2>Кредиторы</h2>

      <div className="toolbar">
        <label className="field field-inline">
          <span>Добавить кредитора по ИНН</span>
          <input
            value={importInn}
            inputMode="numeric"
            placeholder="10 или 12 цифр"
            disabled={readOnly}
            onChange={(e) => setImportInn(e.target.value.replace(/\D/g, '').slice(0, 12))}
          />
        </label>
        <button
          type="button"
          className="link-button"
          disabled={readOnly || (importInn.length !== 10 && importInn.length !== 12)}
          onClick={importByInn}
        >
          Найти и добавить в справочник
        </button>
        {importMessage && <span className="hint">{importMessage}</span>}
      </div>

      {errorFor('creditors') && <p className="error-text">{errorFor('creditors')}</p>}

      <DataGrid
        rows={profile.creditors}
        columns={columns}
        readOnly={readOnly}
        errors={rowErrors}
        getRowId={(row) => row.id}
        createRow={() => ({
          id: newRowId(),
          obligationType: '',
          name: '',
          address: '',
          basis: '',
          basisDetails: '',
          amountDebt: 0,
          confirmingDocument: '',
          isBusiness: false,
          overdueOverThreeMonths: false,
        })}
        onChange={(apply) => update((p) => ({ ...p, creditors: apply(p.creditors) }))}
        emptyText="Кредиторы не добавлены. Список кредиторов не может быть пустым — генерация будет заблокирована."
        footer={
          <span className="totals">
            Задолженность перед кредиторами: <strong>{money(totals.creditorsTotal).digitsWithKopecks} ₽</strong>
          </span>
        }
      />

      <h3>Обязательные платежи</h3>
      <p className="hint">
        Налоги, сборы и взносы. В форме приказа № 530 для них предусмотрена отдельная таблица, но в общую сумму
        задолженности недоимка входит и попадает в текст заявления.
      </p>

      <DataGrid<ObligatoryPayment>
        rows={profile.obligatoryPayments}
        readOnly={readOnly}
        getRowId={(row) => row.id}
        onChange={(apply) => update((p) => ({ ...p, obligatoryPayments: apply(p.obligatoryPayments) }))}
        createRow={() => ({ id: newRowId(), name: '', arrears: 0, isBusiness: false, confirmingDocument: '' })}
        emptyText="Обязательных платежей нет — разделы «Обязательные платежи» формы будут выведены с прочерками."
        columns={[
          {
            key: 'name',
            title: 'Наименование налога, сбора или иного обязательного платежа',
            width: 44,
            type: 'multiline',
            get: (row) => row.name,
            set: (row, value) => ({ ...row, name: value }),
          },
          {
            key: 'arrears',
            title: 'Недоимка, ₽',
            width: 16,
            type: 'money',
            align: 'right',
            get: (row) => kopecksToText(row.arrears),
            set: (row, value) => ({ ...row, arrears: textToKopecks(value) }),
          },
          {
            key: 'confirmingDocument',
            title: 'Подтверждающий документ',
            hint: 'Выберите из списка или впишите своё: перечень не закрыт',
            width: 26,
            type: 'combo',
            options: documents,
            get: (row) => row.confirmingDocument,
            set: (row, value) => ({ ...row, confirmingDocument: value }),
          },
          {
            key: 'isBusiness',
            title: 'Предприн.',
            width: 14,
            type: 'checkbox',
            align: 'center',
            get: (row) => row.isBusiness,
            set: (row, value) => ({ ...row, isBusiness: Boolean(value) }),
          },
        ]}
        footer={
          <span className="totals">
            Недоимка: <strong>{money(totals.obligatoryTotal).digitsWithKopecks} ₽</strong>
          </span>
        }
      />

      <p className="totals-final">
        Общая сумма задолженности: <strong>{money(totals.total).full}</strong>.
        Рассчитывается по таблицам и вручную не редактируется.
      </p>
    </section>
  );
}
